import React from 'react';
import { Notifier } from '@airbrake/browser';

// Centralized Airbrake service configuration
class AirbrakeService {
  private static instance: AirbrakeService;
  private notifier: Notifier | null = null;

  private constructor() {
    this.initializeAirbrake();
  }

  public static getInstance(): AirbrakeService {
    if (!AirbrakeService.instance) {
      AirbrakeService.instance = new AirbrakeService();
    }
    return AirbrakeService.instance;
  }

  private initializeAirbrake(): void {
    const projectId = process.env.REACT_APP_AIRBRAKE_PROJECT_ID;
    const projectKey = process.env.REACT_APP_AIRBRAKE_PROJECT_KEY;

    if (projectId && projectKey) {
      this.notifier = new Notifier({
        projectId: parseInt(projectId, 10),
        projectKey: projectKey,
        environment: process.env.NODE_ENV || 'development'
      });

      // Set user context if available
      const userInfo = this.getUserInfo();
      if (userInfo) {
        this.notifier.addFilter((notice) => {
          notice.context.user = userInfo;
          return notice;
        });
      }
    } else {
      console.warn('Airbrake credentials not found in environment variables');
    }
  }

  private getUserInfo(): any {
    // You can customize this to get user info from your auth context
    try {
      const userString = localStorage.getItem('user');
      return userString ? JSON.parse(userString) : null;
    } catch {
      return null;
    }
  }

  public notify(error: any, params?: any): void {
    if (this.notifier) {
      this.notifier.notify({
        error: error,
        params: params || {}
      });
    } else {
      console.error('Airbrake not initialized:', error);
    }
  }

  public setUser(user: any): void {
    if (this.notifier) {
      this.notifier.addFilter((notice) => {
        notice.context.user = user;
        return notice;
      });
    }
  }
}

// Export singleton instance
export const airbrakeService = AirbrakeService.getInstance();

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to Airbrake
    airbrakeService.notify(error, {
      errorInfo,
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Algo deu errado</h1>
          <p>Ocorreu um erro inesperado. Nossa equipe foi notificada.</p>
          <button onClick={this.resetError} style={{ 
            padding: '10px 20px', 
            backgroundColor: '#007bff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Tentar novamente
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;